import { formatDateToDateString, isSameDay } from "@components/inputs/date/utils";
import { getNewItemsMaxId } from "@odata/Data.utils";
import {
    IPrWorkingPatternDayEntity,
    IPrWorkingPatternDayIntervalEntity,
    IPrWorkingPatternEntity,
    PrWorkingPatternEntity
} from "@odata/GeneratedEntityTypes";
import { PrWorkingPatternIntervalTypeCode, PrWorkingPatternTypeCode } from "@odata/GeneratedEnums";
import { getHoursFromTimeStamp } from "@pages/payroll/attendance/Attendance.utils";
import { getTimeSpanFromHours } from "@pages/payroll/workingPatterns/WorkDayIntervals.utils";
import {
    createDefaultIntervalsForDay,
    deleteIntervalErrors,
    getNewEmptyIntervalLineItem,
    recalculateWeeklyWorkingHours
} from "@pages/payroll/workingPatterns/WorkingPatterns.utils";
import { isNotDefined } from "@utils/general";
import { Dayjs } from "dayjs";
import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";

import CalendarWithInputs from "../../../components/calendarWithInputs/CalendarWithInputs";
import BindingContext from "../../../odata/BindingContext";
import { getUtcDayjs } from "../../../types/Date";
import { FormStorage } from "../../../views/formView/FormStorage";
import { CalendarFieldWrapper, WeekContentAfter } from "./WorkingPattern.styles";
import { dateStartPath, rotationLengthPath } from "./WorkingPatternsDef";
import { IWorkingPatternCustomData } from "./WorkingPatternsFormView";

interface IProps {
    storage: FormStorage<IPrWorkingPatternEntity, IWorkingPatternCustomData>;
    isReadOnly?: boolean;
}

const SmartCalendarWrapper: React.FC<IProps> = ({ storage, isReadOnly }) => {
    const { t } = useTranslation("WorkingPatterns");

    const createDefaultIntervals = useCallback((workingHours: number): IPrWorkingPatternDayIntervalEntity[] => {
        return createDefaultIntervalsForDay(storage, workingHours);
    }, [storage]);

    const handleSelect = useCallback((day: Dayjs): void => {
        if (isNotDefined(day)) {
            storage.setCustomData({ selectedDay: null });
            storage.refresh();
            return;
        }

        const entity = storage.getEntity();
        const editedDay = entity.Days?.find(d => isSameDay(day, getUtcDayjs(d.Date)));
        const daysBc = storage.data.bindingContext.navigate(PrWorkingPatternEntity.Days);

        const newEmptyInterval = {
            ...getNewEmptyIntervalLineItem(storage),
            Type: {
                Code: PrWorkingPatternIntervalTypeCode.Work
            }
        };

        if (editedDay) {
            const bc = daysBc.addKey(editedDay);
            if (!editedDay.Intervals?.length) {
                editedDay.Intervals = [newEmptyInterval];
            }
            storage.setCustomData({ selectedDay: bc });
        } else {
            const newItemsMax = getNewItemsMaxId(entity.Days);
            const newDay = BindingContext.createNewEntity<IPrWorkingPatternDayEntity>(newItemsMax + 1, {
                Date: day.toDate(),
                Intervals: [newEmptyInterval]
            });
            entity.Days.push(newDay);
            const bc = storage.data.bindingContext.navigate(PrWorkingPatternEntity.Days).addKey(newDay);
            storage.setCustomData({ selectedDay: bc });
        }
        storage.refresh();
    }, [storage]);

    const handleChange = useCallback((val: number, day: Dayjs): void => {
        const entity = storage.getEntity();
        const intervals = createDefaultIntervals(val);

        const editedDay = entity.Days.find(d => isSameDay(day, getUtcDayjs(d.Date)));
        if (editedDay) {
            deleteIntervalErrors(storage, editedDay);

            editedDay.WorkingHours = isNotDefined(val) ? null : getTimeSpanFromHours(val);
            editedDay.Intervals = intervals;
        } else {
            const newItemsMax = getNewItemsMaxId(entity.Days);
            const newDay = BindingContext.createNewEntity<IPrWorkingPatternDayEntity>(newItemsMax + 1, {
                Date: day.toDate(),
                WorkingHours: getTimeSpanFromHours(val),
                Intervals: intervals
            });
            entity.Days.push(newDay);
            handleSelect(day);
        }
        recalculateWeeklyWorkingHours(storage);
        storage.refresh();
    }, [storage, handleSelect, createDefaultIntervals]);

    function daysValueMap(): Record<string, number> {
        const entity = storage.data.entity as IPrWorkingPatternEntity;

        return (entity.Days ?? []).reduce((map, day) => {
            map[formatDateToDateString(day.Date)] = getHoursFromTimeStamp(day.WorkingHours);
            return map;
        }, {} as Record<string, number>);
    }

    const type = storage.getValueByPath(PrWorkingPatternEntity.TypeCode);
    const rotationLength = Math.min(storage.getValueByPath(rotationLengthPath), 365);
    const isDifferentOddAndEvenWeek = storage.getValueByPath(PrWorkingPatternEntity.IsDifferentOddAndEvenWeek);
    const startDate = storage.getValueByPath(dateStartPath);
    const selectedBc = storage.getCustomData()?.selectedDay;
    let selectedDate: Date = null;
    if (selectedBc) {
        const day: IPrWorkingPatternDayEntity = storage.getValue(selectedBc);
        selectedDate = day?.Date ? getUtcDayjs(day.Date).toDate() : null;
    }

    return <CalendarFieldWrapper>
        <CalendarWithInputs
            type={type}
            selectedDay={selectedDate}
            onChange={handleChange}
            onSelect={handleSelect}
            rotationLength={rotationLength}
            startDate={startDate ? getUtcDayjs(startDate) : null}
            isDifferentOddAndEvenWeek={isDifferentOddAndEvenWeek}
            daysValuesMap={daysValueMap()}
            isReadOnly={isReadOnly}
        />
        {type === PrWorkingPatternTypeCode.Weekly && isDifferentOddAndEvenWeek &&
            <WeekContentAfter>
                <div>{t("WorkingPatterns:Even")}</div>
                <div>{t("WorkingPatterns:Odd")}</div>
            </WeekContentAfter>
        }
    </CalendarFieldWrapper>;
};

export default SmartCalendarWrapper;